import { motion } from 'motion/react';
import { ArrowRight, Clock, Globe, Zap, Brain, Mail, MessageSquare, CheckCircle, Star, Briefcase, Languages, Search } from 'lucide-react';
import { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { AuthModal } from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { trackPlanSelected } from '@/lib/analytics';

export function ForJobSeekersPage() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    trackPlanSelected('free', 'monthly', 0);
    if (isAuthenticated) {
      navigate('/account');
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleViewPricing = () => {
    navigate('/pricing');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F2847] via-[#1a1a2e] to-[#16213e]">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-semibold mb-6 border border-emerald-500/30">
                For IT Professionals & Job Seekers
              </span>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Land Your Dream Job<br />
                <span className="text-emerald-400">With Perfect Emails</span>
              </h1>
              <p className="text-xl sm:text-2xl text-white/80 max-w-3xl mx-auto mb-4">
                Stop spending hours crafting the perfect email to recruiters and hiring managers.
                xReplAI generates polished, professional drafts in seconds -- in flawless English, every time.
              </p>
              <p className="text-lg text-white/60 max-w-2xl mx-auto mb-10">
                Whether English is your first language or your third, xReplAI ensures every message you send sounds like it came from a native professional.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-500/90 transition-all shadow-lg shadow-emerald-500/30 text-lg"
                >
                  Get Started Free
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={handleViewPricing}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/20 text-lg"
                >
                  Compare Plans
                </button>
              </div>
              <p className="text-sm text-white/50 mt-4">
                Free plan includes 30 generations/month. No credit card required.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                The Job Search Email Struggle
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Sound familiar? You're not alone.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Clock,
                  title: '45 minutes per application email',
                  description: 'You rewrite the same email over and over, second-guessing every word. Is it too formal? Too casual? Does it sound natural?',
                  stat: '85%',
                  statLabel: 'of job seekers say writing emails is the most stressful part',
                },
                {
                  icon: Languages,
                  title: '"Does this sound right in English?"',
                  description: 'You know you\'re qualified. But translating your expertise into polished, professional English emails feels like a second job.',
                  stat: '3.2x',
                  statLabel: 'longer for non-native speakers to write professional emails',
                },
                {
                  icon: Search,
                  title: 'Dozens of emails, zero replies',
                  description: 'Recruiters read hundreds of messages daily. Generic, awkward, or poorly written emails get ignored instantly -- no matter how qualified you are.',
                  stat: '70%',
                  statLabel: 'of recruiter outreach emails never get a response',
                },
              ].map((pain, i) => (
                <motion.div
                  key={i}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <pain.icon size={32} className="text-red-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{pain.title}</h3>
                  <p className="text-white/70 mb-6">{pain.description}</p>
                  <div className="border-t border-white/10 pt-4">
                    <span className="text-3xl font-bold text-emerald-400">{pain.stat}</span>
                    <p className="text-xs text-white/50 mt-1">{pain.statLabel}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* The Unfair Advantage Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Your Unfair Advantage in the Job Market
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                xReplAI doesn't just fix your grammar -- it writes complete professional emails that impress hiring managers
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: Globe,
                  title: 'Native-Level Professional English',
                  description: 'Every draft reads like it was written by a native English speaker with years of corporate experience. Perfect grammar, natural idioms, professional tone.',
                  highlight: 'Sound like a native speaker',
                },
                {
                  icon: Zap,
                  title: 'From Blank Screen to Polished Email in 10 Seconds',
                  description: 'Stop staring at a blank compose window. Click one button and get 2-4 complete draft options. Pick the best one, tweak if needed, and send.',
                  highlight: '10 seconds vs. 45 minutes',
                },
                {
                  icon: Brain,
                  title: 'Context-Aware Responses',
                  description: 'Replying to a recruiter? Following up after an interview? Negotiating an offer? xReplAI reads the conversation and tailors the response to the exact situation.',
                  highlight: 'Knows what to say and when',
                },
                {
                  icon: MessageSquare,
                  title: 'Works in Gmail & LinkedIn',
                  description: 'Use it right where you communicate with recruiters and hiring managers. No copying, no pasting, no switching tabs. One click inside your inbox or LinkedIn messages.',
                  highlight: 'Chrome extension, zero friction',
                },
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <feature.icon size={24} className="text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                      <p className="text-white/70 mb-3">{feature.description}</p>
                      <span className="inline-block px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm font-medium border border-emerald-500/20">
                        {feature.highlight}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Every Job Search Email, Handled
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                xReplAI generates professional drafts for every stage of your job search
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Cold outreach to recruiters', description: 'Introduce yourself professionally and express interest in open roles' },
                { title: 'Respond to recruiter messages', description: 'Reply to LinkedIn InMails and emails with confidence and clarity' },
                { title: 'Follow up after applications', description: 'Polite, persistent follow-ups that keep you top-of-mind' },
                { title: 'Thank you after interviews', description: 'Thoughtful post-interview emails that reinforce your candidacy' },
                { title: 'Salary negotiations', description: 'Professionally articulate your expectations without being pushy' },
                { title: 'Networking introductions', description: 'Reach out to connections for referrals with the right tone' },
              ].map((useCase, i) => (
                <motion.div
                  key={i}
                  className="bg-white/5 rounded-xl p-6 border border-white/10"
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Briefcase size={20} className="text-emerald-400 mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-2">{useCase.title}</h3>
                  <p className="text-sm text-white/60">{useCase.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                3 Steps to Better Job Search Emails
              </h2>
              <p className="text-lg text-white/70">
                Install once, use everywhere you communicate with recruiters
              </p>
            </motion.div>

            <div className="space-y-8">
              {[
                { step: '1', title: 'Install the Chrome extension (2 minutes)', description: 'Add xReplAI from the Chrome Web Store. It works instantly in Gmail and LinkedIn -- no configuration needed.' },
                { step: '2', title: 'Open any recruiter email or LinkedIn message', description: 'When you click reply or compose, xReplAI reads the conversation context and understands what kind of response is needed.' },
                { step: '3', title: 'Pick a draft and send with confidence', description: 'Get 2-4 professionally written draft options. Choose the one that fits, make any tweaks, and hit send -- knowing your English is perfect.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-6"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-emerald-500/30">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-white/70">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                The Edge You Need in a Competitive Market
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  metric: '10x',
                  label: 'Faster email writing',
                  description: 'What took 45 minutes now takes under a minute',
                },
                {
                  metric: '100%',
                  label: 'Professional English',
                  description: 'Every email reads like it was written by a native speaker',
                },
                {
                  metric: '30',
                  label: 'Free drafts per month',
                  description: 'Enough to power your entire active job search -- for free',
                },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  className="text-center p-8 bg-white/5 rounded-2xl border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-4xl font-bold text-emerald-400 mb-2">{stat.metric}</div>
                  <div className="text-lg font-semibold text-white mb-2">{stat.label}</div>
                  <div className="text-sm text-white/60">{stat.description}</div>
                </motion.div>
              ))}
            </div>

            {/* Testimonial */}
            <motion.div
              className="mt-12 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-8 border border-white/10 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Star size={32} className="text-yellow-400 mx-auto mb-4" />
              <blockquote className="text-lg text-white/90 italic mb-4 max-w-2xl mx-auto">
                "I was sending 20+ emails a day to recruiters and spending my entire morning just writing them. With xReplAI, I draft all my responses in minutes. The English sounds perfect every time -- I actually got compliments from a US hiring manager on my communication skills."
              </blockquote>
              <p className="text-white/60 text-sm">
                -- Senior Software Engineer, Job Seeker
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Teaser */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Start Free -- Upgrade If You Need More
              </h2>
              <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
                30 free email drafts per month is enough for most active job searches. Need more? Pro gives you 500 generations, style matching, and premium AI models.
              </p>

              <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-2">Free</h3>
                  <div className="text-3xl font-bold text-white mb-1">$0</div>
                  <p className="text-sm text-white/60 mb-4">forever</p>
                  <ul className="text-left text-sm text-white/70 space-y-2">
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> 30 generations/month</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> Gmail + LinkedIn</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> Professional English drafts</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> 2 draft variants</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-600/30 rounded-2xl p-6 border border-emerald-500/50 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 text-white rounded-full text-xs font-semibold">
                    Best Value
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Pro</h3>
                  <div className="text-3xl font-bold text-white mb-1">$30<span className="text-lg font-normal">/mo</span></div>
                  <p className="text-sm text-white/70 mb-4">or $252/year (save 30%)</p>
                  <ul className="text-left text-sm text-white/90 space-y-2">
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> 500 generations/month</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> Style matching</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> 4 draft variants</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> GPT-4o + Claude + Grok</li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-500/90 transition-all shadow-lg shadow-emerald-500/30"
                >
                  Get Started Free
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={handleViewPricing}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/20"
                >
                  Compare All Plans
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Your Skills Got You Here.<br />
                <span className="text-emerald-400">Let Your Emails Land the Job.</span>
              </h2>
              <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
                Don't let an email be the reason you don't get the interview. Install xReplAI in 2 minutes and never stress about professional English again.
              </p>
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-emerald-500 text-white font-bold rounded-lg hover:bg-emerald-500/90 transition-all shadow-lg shadow-emerald-500/30 text-lg"
              >
                Start Free Today
                <ArrowRight size={22} />
              </button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </div>
  );
}
